/**
 * Aviso de MENSAGEM NOVA no WhatsApp, em qualquer tela do cockpit, separado por
 * casa. [Bruno 2026-10-13]
 *
 * Chegou mensagem de cliente no número do Meu Contador → cartão azul "Meu
 * Contador · nova mensagem" + som; no da NivelOn → cartão verde + outro tom.
 * Clicar abre a conversa na casa certa. Com a aba em segundo plano, vai também
 * a notificação do sistema (se a pessoa permitiu). Cada pessoa liga/desliga os
 * avisos de cada casa em "🔔 Avisos" (na tela do WhatsApp).
 *
 * Fonte: o mesmo SSE do atendimento ao vivo (/api/whatsapp/stream), que agora
 * diz a casa de cada mensagem que chegou (`novas`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as aoVivoWhats from '../lib/whatsappAoVivo';
import { CASAS_AVISO, lerAvisos, gravarAvisos, tocarAviso, textoDoAviso, notificarSistema, pedirPermissao } from '../lib/avisoWhatsapp';

function papel() {
  try { return (JSON.parse(localStorage.getItem('usuario') || 'null') || {}).papel; } catch (_) { return null; }
}

export default function AvisoWhatsapp() {
  const p = papel();
  if (p === 'parceiro_societario' || p === 'departamento_pessoal') return null;
  // [Pablo 2026-10-27] A Onésia também é avisada (som + alerta), mas só das
  // conversas dela: o servidor recorta o canal ao vivo para o papel dela.
  return <AvisoInterno pc={p === 'primeiro_contato'} />;
}

const ROTA_PC = '/escritorio/primeiro-contato';
const TITULO_ORIGINAL = typeof document !== 'undefined' ? document.title : '';

/** Alerta mais forte para quem atende só pelo WhatsApp: três toques seguidos. */
function tocarForte(casa) {
  tocarAviso(casa);
  setTimeout(() => tocarAviso(casa), 550);
  setTimeout(() => tocarAviso(casa), 1100);
}

function AvisoInterno({ pc = false }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [cartoes, setCartoes] = useState([]);
  const ondeRef = useRef({ pathname, search });
  ondeRef.current = { pathname, search };

  const abrir = useCallback((n) => {
    setCartoes((cs) => cs.filter((c) => c.conversa_id !== n.conversa_id));
    navigate(pc
      ? `${ROTA_PC}?casa=${n.casa}&aba=conversas&conversa=${n.conversa_id}`
      : `/escritorio/whatsapp?casa=${n.casa}&conversa=${n.conversa_id}`);
    setTimeout(() => window.dispatchEvent(new CustomEvent('cockpit:abrir-conversa', { detail: Number(n.conversa_id) })), 0);
  }, [navigate, pc]);

  useEffect(() => {
    // Mesma conexão da tela do WhatsApp (lib/whatsappAoVivo), que volta sozinha
    // depois de cada publicação; antes o aviso parava até alguém dar F5.
    const soltar = aoVivoWhats.usar();
    const parar = aoVivoWhats.ouvir('mensagem', (d) => {
      const novas = (d && d.novas) || [];
      if (!novas.length) return;
      const pref = lerAvisos();
      const onde = ondeRef.current;
      const q = new URLSearchParams(onde.search);
      const casaNaTela = onde.pathname.startsWith('/escritorio/whatsapp')
        ? (q.get('casa') || 'meu_contador')
        : (pc && onde.pathname.startsWith(ROTA_PC) && q.get('aba') === 'conversas' ? (q.get('casa') || 'nivelon') : null);
      let tocou = false;
      for (const n of novas) {
        if (!CASAS_AVISO[n.casa] || pref[n.casa] === false) continue;
        if (pref.som && !tocou) { (pc ? tocarForte : tocarAviso)(n.casa); tocou = true; }
        notificarSistema(n, () => abrir(n));
        // Já está olhando o WhatsApp dessa casa: a lista atualiza sozinha, sem cartão.
        if (casaNaTela === n.casa && document.visibilityState === 'visible') continue;
        const id = `${n.conversa_id}-${n.id}`;
        setCartoes((cs) => [...cs.filter((c) => c.conversa_id !== n.conversa_id), { ...n, chave: id }].slice(-3));
        // Para ela o cartão fica até ser aberto ou fechado (não some sozinho).
        if (!pc) setTimeout(() => setCartoes((cs) => cs.filter((c) => c.chave !== id)), 10000);
      }
    });
    return () => { parar(); soltar(); };
  }, [abrir, pc]);

  // Título da aba piscando enquanto houver aviso aberto (dá pra ver de outra aba).
  useEffect(() => {
    if (!pc || !cartoes.length) { if (pc) document.title = TITULO_ORIGINAL; return undefined; }
    let par = false;
    const t = setInterval(() => {
      par = !par;
      document.title = par ? `(${cartoes.length}) 💬 Mensagem nova` : TITULO_ORIGINAL;
    }, 1000);
    return () => { clearInterval(t); document.title = TITULO_ORIGINAL; };
  }, [pc, cartoes.length]);

  if (!cartoes.length) return null;
  return (
    <div role="region" aria-label="Mensagens novas no WhatsApp" aria-live="polite" style={{
      position: 'fixed', ...(pc ? { bottom: 24 } : { top: 64 }), right: 18, zIndex: 2400, width: pc ? 420 : 340, maxWidth: 'calc(100vw - 36px)',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      {cartoes.map((n) => {
        const c = CASAS_AVISO[n.casa];
        const t = textoDoAviso(n);
        return (
          <div key={n.chave} style={{
            display: 'flex', alignItems: 'stretch', background: 'var(--paper, #fff)', color: 'var(--ink, #15171C)',
            borderRadius: 12, border: pc ? `2px solid ${c.cor}` : '1px solid var(--line, #d9dee8)', borderLeft: `${pc ? 10 : 5}px solid ${c.cor}`,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)', overflow: 'hidden',
          }}>
            <button type="button" onClick={() => abrir(n)} style={{
              flex: 1, minWidth: 0, textAlign: 'left', border: 0, background: 'transparent', color: 'inherit',
              font: 'inherit', padding: pc ? '14px 16px' : '10px 12px', cursor: 'pointer',
            }}>
              <span style={{ display: 'block', fontSize: pc ? 14 : 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: c.cor }}>
                💬 {t.titulo}
              </span>
              <span style={{ display: 'block', fontWeight: 700, fontSize: pc ? 19 : 14, margin: '2px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.quem}{n.grupo ? ' (grupo)' : ''}
              </span>
              <span style={{ display: 'block', fontSize: pc ? 16 : 13, color: 'var(--ink-soft, #555)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t.corpo}
              </span>
              {pc && <span style={{ display: 'inline-block', marginTop: 8, padding: '6px 14px', borderRadius: 999, background: c.cor, color: '#fff', fontWeight: 800, fontSize: 15 }}>Abrir conversa</span>}
            </button>
            <button type="button" aria-label="Fechar aviso" onClick={() => setCartoes((cs) => cs.filter((x) => x.chave !== n.chave))} style={{
              border: 0, background: 'transparent', color: 'var(--ink-soft, #777)', cursor: 'pointer', padding: '0 12px', fontSize: pc ? 26 : 18,
            }}>×</button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * "🔔 Avisos": liga/desliga por casa, som e notificação do sistema. Fica na
 * tela do WhatsApp e no atendimento do celular. `escuro` = botão no topo colorido.
 */
export function PreferenciasAviso({ escuro = false, compacto = false, className = '' }) {
  const [pref, setPref] = useState(lerAvisos);
  const [aberto, setAberto] = useState(false);
  const [perm, setPerm] = useState(typeof window.Notification === 'undefined' ? 'unsupported' : window.Notification.permission);
  useEffect(() => {
    const ouvir = (e) => setPref(e.detail || lerAvisos());
    window.addEventListener('wa-avisos-mudou', ouvir);
    return () => window.removeEventListener('wa-avisos-mudou', ouvir);
  }, []);
  const muda = (k) => { const n = { ...pref, [k]: !pref[k] }; setPref(n); gravarAvisos(n); };
  const ligados = ['meu_contador', 'nivelon'].filter((k) => pref[k]).length;
  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button type="button" onClick={() => setAberto((v) => !v)} aria-expanded={aberto} title="Avisos de mensagem nova"
        aria-label="Avisos de mensagem nova"
        className={className || undefined}
        style={className ? undefined : escuro ? {
          height: 34, minWidth: 34, padding: compacto ? '0 8px' : '0 10px', borderRadius: 999, border: '1px solid rgba(255,255,255,0.45)',
          background: 'rgba(255,255,255,0.1)', color: '#fff', font: 'inherit', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap',
        } : {
          font: 'inherit', fontSize: '0.76rem', cursor: 'pointer', padding: '5px 12px', borderRadius: 20,
          border: '1px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)', whiteSpace: 'nowrap',
        }}>
        {className ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 16V11a6 6 0 0112 0v5l1.5 2h-15L6 16zM10 20.5a2 2 0 004 0" />
            {!ligados && <path d="M4 4l16 16" />}
          </svg>
        ) : <>{ligados ? '🔔' : '🔕'}{compacto ? '' : ' Avisos'}</>}
      </button>
      {aberto && (
        <div role="dialog" aria-label="Avisos de mensagem nova" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 3000, width: 270,
          background: 'var(--paper, #fff)', color: 'var(--ink, #15171C)', border: '1px solid var(--line, #d9dee8)',
          borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.2)', padding: 12, textAlign: 'left',
        }}>
          <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Avisar quando chegar mensagem</div>
          {Object.entries(CASAS_AVISO).map(([k, c]) => (
            <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!pref[k]} onChange={() => muda(k)} />
              <i style={{ width: 10, height: 10, borderRadius: '50%', background: c.cor, display: 'inline-block' }} />
              {c.nome}
            </label>
          ))}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 14, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!pref.som} onChange={() => muda('som')} /> Com som
          </label>
          <div style={{ borderTop: '1px solid var(--line, #d9dee8)', marginTop: 6, paddingTop: 8, fontSize: 12.5, color: 'var(--ink-soft, #555)' }}>
            {perm === 'granted' ? 'Notificação do sistema: ligada (aparece mesmo com a tela em outra aba).'
              : perm === 'denied' ? 'Notificação do sistema bloqueada no navegador. Libere nas configurações do site.'
                : perm === 'unsupported' ? 'Este navegador não mostra notificação do sistema (no iPhone, só com o atalho na tela de início).'
                  : (
                    <button type="button" className="btn btn-primary btn-sm" onClick={async () => setPerm(await pedirPermissao())}>
                      Permitir notificação do sistema
                    </button>
                  )}
          </div>
          <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--ink-soft, #777)' }}>Vale só para você, neste aparelho.</div>
        </div>
      )}
    </span>
  );
}
